import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "morning-prayer",
  title: "A morning prayer — before the phone, before the day",
  h1: "A morning prayer",
  description:
    "A prayer to say before you have looked at anything, plus four passages on why the first minutes decide the shape of the rest of the day.",
  targets: [
    "morning prayer",
    "prayer to start the day",
    "short morning prayer",
    "prayer before the day starts",
  ],
  published: "2026-09-03",
  standfirst:
    "The first thing you attend to sets the terms for everything after it. Most mornings that thing is a lock screen.",
  takeaways: [
    "Say it before you unlock anything. A morning prayer said after twenty minutes of scrolling is a different prayer — it is asking for recovery, not for a beginning.",
    "Psalm 5:3 has a second half nobody quotes: after laying out the requests, the psalmist watches expectantly. Asking and then not looking for an answer is half the practice.",
    "It does not have to be long. Sixty seconds said before the day starts beats twenty minutes said when you have already lost.",
  ],
  when: [
    "Before the first unlock, while the phone is still face down.",
    "In the kitchen, waiting for the kettle.",
    "In the car before you drive, rather than after you arrive.",
    "On the mornings you woke up already behind.",
  ],
  prayer: [
    "God, before anything else has my attention, you do. I have not read anything yet. I have not been made angry by anything yet. This is the only part of the day that is still quiet, and I am giving it to you first on purpose.",
    "I do not know what is in today. You do. Give me enough for it — not the whole picture, just the next thing and the willingness to do it.",
    "Where I am going to be tempted to hide in my phone, let me notice it happening. Not stop it by force. Just notice.",
    "Make me useful to someone before the day is out. Show me who.",
    "And when I get it wrong, which I will, let me come back to this without a speech about how I should have done better.",
  ],
  short:
    "God, you have my attention before anything else does. Give me enough for today.",
  honest: [
    "This prayer does not promise a good day. Plenty of people have prayed something like it and then had the worst day of their year; the psalms are full of mornings that went badly afterwards.",
    "It is also not a productivity technique. If the reason for praying it is to perform better, the prayer has been turned into a tool and it will stop working the first week it does not deliver.",
    "And it does not require you to be a morning person. The hour is not the point. The order is: this before the feed, whenever this happens to be.",
  ],
  verses: [
    {
      verse: {
        text: "Yahweh, in the morning you will hear my voice. In the morning I will lay my requests before you, and will watch expectantly.",
        ref: "Psalm 5:3",
      },
      note: "Two halves, and almost everyone quotes the first. The requests are laid out — the verb is the one used for arranging wood on an altar, deliberate and in order — and then the psalmist waits and watches for what happens. A morning prayer with no expectation attached to it is a monologue.",
    },
    {
      verse: {
        text: "Cause me to hear your loving kindness in the morning, for I trust in you. Cause me to know the way in which I should walk, for I lift up my soul to you.",
        ref: "Psalm 143:8",
      },
      note: "Written by somebody hiding from people trying to kill him, which is worth knowing before it gets read as a gentle sunrise devotional. The request is for two specific things in a specific order: hear something true first, then know where to put your feet. Direction comes after reassurance, not instead of it.",
    },
    {
      verse: {
        text: "It is because of Yahweh's loving kindnesses that we are not consumed, because his mercies don't fail. They are new every morning. Great is your faithfulness.",
        ref: "Lamentations 3:22–23",
      },
      note: "The most-quoted line in a book that is otherwise five chapters of a man watching his city burn. New every morning is not a cheerful sentiment here; it is the one thing he can still find to say after describing a famine. Which is precisely why it holds on the mornings the cheerful version does not.",
    },
    {
      verse: {
        text: "Early in the morning, while it was still dark, he rose up and went out, and departed into a deserted place, and prayed there.",
        ref: "Mark 1:35",
      },
      note: "The verse before it has the whole town at the door and the verse after has the disciples coming to find him because everyone is looking. He went somewhere he could not be reached, before anyone could reach him. The deserted place is the point, and a phone in the room is the opposite of one.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The first input wins",
      id: "the-first-input-wins",
    },
    {
      type: "p",
      text: "Whatever you attend to first is what the rest of the morning is measured against. Open a feed and the day begins mid-argument, at somebody else's pitch, about something you did not choose. It is very hard to walk that back at nine.",
    },
    {
      type: "p",
      text: "This is the practical case for praying before unlocking rather than after breakfast: not that early is holier, but that the first thing is structurally different from the second thing. A {morning routine} that starts with a prayer and ends with the feed is a different day from one that runs the other way round.",
      links: {
        "morning routine": "/blog/morning-quiet-time-routine",
      },
    },
    {
      type: "h2",
      text: "Sixty seconds is a real prayer",
      id: "sixty-seconds-is-a-real-prayer",
    },
    {
      type: "p",
      text: "The reason most morning prayer collapses is that the version people imagine is half an hour with a journal, and half an hour does not exist on a Tuesday. So nothing happens at all, and the failure gets filed under discipline rather than under planning.",
    },
    {
      type: "p",
      text: "A minute, said standing up, before the phone, every day, beats an hour on Sunday. That is not a concession to modern life; it is how nearly every durable practice in Christian history has actually been kept by people with work to do.",
    },
    {
      type: "note",
      text: "If the phone is the thing that keeps winning the first five minutes, the fix is mechanical rather than moral. Put it in another room overnight, or put something in front of it that has to be cleared first.",
    },
    {
      type: "h2",
      text: "Watch expectantly",
      id: "watch-expectantly",
    },
    {
      type: "p",
      text: "The second half of Psalm 5:3 is the part that changes a morning prayer from a ritual into a relationship. Having asked, look for the answer during the day — in a conversation that goes better than it should have, in the thing you were dreading turning out to be small.",
    },
    {
      type: "p",
      text: "That watching is also what makes the practice survive a bad week. If you never look, you never notice, and a prayer you have never noticed being answered is a prayer that is very easy to stop saying.",
    },
  ],
  faq: [
    {
      q: "What is a good short morning prayer?",
      a: 'One sentence, said before you unlock anything: "God, you have my attention before anything else does — give me enough for today." Sixty seconds said daily is worth more than twenty minutes said occasionally.',
    },
    {
      q: "Does it matter what time I pray in the morning?",
      a: "Not really. What matters is the order — before the feed rather than after it. Mark 1:35 has Jesus praying before dawn, but the detail the passage draws attention to is that he went somewhere he could not be reached.",
    },
    {
      q: "What if I forget until I have already been on my phone?",
      a: "Pray it anyway. The prayer that follows a lost twenty minutes is doing real work; it is just doing recovery rather than beginning. Move the phone tonight so tomorrow is different.",
    },
  ],
  relatedPrayers: [
    "prayer-before-bed",
    "prayer-for-focus",
    "prayer-before-work",
  ],
  related: ["morning-quiet-time-routine", "how-to-stop-scrolling-at-night"],
  relatedTopics: ["bible-verses-about-time"],
  relatedPassages: ["psalm-23"],
};
